import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  loadThemeModeFromStorage,
  saveThemeModeToStorage,
  ThemeMode,
  ThemeModeContext,
  ThemeModeContextType,
} from '../../context/ThemeModeContext';

interface ThemeModeProviderProps {
  children: ReactNode;
}

const getSystemPreference = (): 'light' | 'dark' =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const resolveMode = (mode: ThemeMode): 'light' | 'dark' =>
  mode === 'auto' ? getSystemPreference() : mode;

export const ThemeModeProvider: React.FC<ThemeModeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(loadThemeModeFromStorage);
  const [resolvedMode, setResolvedMode] = useState<'light' | 'dark'>(() => resolveMode(loadThemeModeFromStorage()));

  // Keep resolvedMode in sync when themeMode changes
  useEffect(() => {
    setResolvedMode(resolveMode(themeMode));
  }, [themeMode]);

  // Listen for OS-level preference changes (only relevant when mode is 'auto')
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (themeMode === 'auto') {
        setResolvedMode(mediaQuery.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  // Sync theme changes across browser tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'scalar-app-theme-mode' && e.newValue) {
        const newMode = e.newValue as ThemeMode;
        setThemeModeState(newMode);
        setResolvedMode(resolveMode(newMode));
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    saveThemeModeToStorage(mode);
  }, []);

  const contextValue: ThemeModeContextType = useMemo(
    () => ({ themeMode, setThemeMode, resolvedMode }),
    [themeMode, setThemeMode, resolvedMode]
  );

  return <ThemeModeContext.Provider value={contextValue}>{children}</ThemeModeContext.Provider>;
};

export default ThemeModeProvider;
